import { useEffect } from "react";
import {
	isClientOutdated,
	markClientOutdated,
} from "@/lib/__shared__/clientOutdated";

// How often to poll `version.json` as a backstop. The primary trigger is a tab
// regaining focus (`visibilitychange`), which is when a long-lived tab is most
// likely to have gone stale across a deploy.
const POLL_INTERVAL_MS = 30 * 60 * 1000;

const VERSION_URL = "/version.json";

async function fetchDeployedVersion(): Promise<string | null> {
	try {
		const response = await fetch(VERSION_URL, { cache: "no-store" });
		if (!response.ok) return null;
		const body = (await response.json()) as { version?: string };
		return body.version ?? null;
	} catch {
		// Offline or a transient network error — treat as "no new info".
		return null;
	}
}

/**
 * Proactively detects when a newer bundle has been deployed while this tab
 * stayed open, and flags the shared outdated-client signal on a mismatch.
 *
 * Compares the build id baked into the running bundle (`__APP_VERSION__`)
 * against the deployed `version.json`. This is the proactive counterpart to the
 * apiClient's reactive `CLIENT_OUTDATED` interceptor; both converge on the same
 * signal and the same refresh prompt.
 *
 * Note: this can only ever protect against a *future* deploy, because the poll
 * ships inside the bundle. A tab already running an older bundle has no poll;
 * the server-side `CLIENT_OUTDATED` guard is what covers those clients when they
 * act.
 */
export function useDeployedVersionPolling(): void {
	useEffect(() => {
		let cancelled = false;

		const check = async () => {
			if (cancelled || isClientOutdated()) return;
			const deployed = await fetchDeployedVersion();
			if (!cancelled && deployed !== null && deployed !== __APP_VERSION__) {
				markClientOutdated();
			}
		};

		const onVisibilityChange = () => {
			if (document.visibilityState === "visible") check();
		};

		document.addEventListener("visibilitychange", onVisibilityChange);
		const intervalId = window.setInterval(check, POLL_INTERVAL_MS);
		check();

		return () => {
			cancelled = true;
			document.removeEventListener("visibilitychange", onVisibilityChange);
			window.clearInterval(intervalId);
		};
	}, []);
}
